import { Box, Button, Flex, Heading, Input, InputGroup, SimpleGrid, Text } from '@chakra-ui/react';
import { UseFormReturn, useController } from 'react-hook-form';
import { useDebouncedFieldRegistry } from '@/utils/filterUtils/useDebouncedFieldRegistry';
import { ChakraFeatherIcon } from 'src/components/ChakraFeatherIcon';
import { SelectField } from 'src/components/SelectField';
import { Filters } from '../../types';
import { FundraisingCampaignStatus } from '@/model';
import { DateRangeFilterFields } from '@/components/DateRangeFilterFields';
import { DateRangeChangeHandler } from '@/components/DateRangeFilterFields/types';
import { useTranslation } from 'react-i18next';

export interface FundraisingCampaignFiltersBoxProps {
  filtersForm: UseFormReturn<Filters>;
}

export const FundraisingCampaignFiltersBox = ({ filtersForm }: FundraisingCampaignFiltersBoxProps) => {
  const { t } = useTranslation();
  const { register, reset, control } = filtersForm;

  const dateValueControl = useController({
    name: 'dateValue',
    control,
  });
  const startDateControl = useController({
    name: 'start_date',
    control,
  });
  const endDateControl = useController({
    name: 'end_date',
    control,
  });

  const handleRangeChange: DateRangeChangeHandler = (startDate, endDate) => {
    startDateControl.field.onChange(startDate);
    endDateControl.field.onChange(endDate);
  };

  return (
    <Box>
      <Flex align='center' gap='pico' mb='deka'>
        <ChakraFeatherIcon color='text.main' name='filter' />
        <Heading variant='sectionTitle'>
          {t('pages.fundraising_campaign.components.fundraising_campaign_filters_box.fundraising_campaign_filters_box_title')}
        </Heading>
      </Flex>

      <Flex direction={{ base: 'column' }} gap='small' rowGap='micro'>
        <Flex gap='deka' flexWrap='wrap'>
          <InputGroup w='fit-content' display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t(
                'pages.fundraising_campaign.components.fundraising_campaign_filters_box.fundraising_campaign_filters_box_campaign_label',
              )}
            </Text>
            <Input variant='flushed' {...useDebouncedFieldRegistry(() => register('name'))} />
          </InputGroup>

          <InputGroup w='fit-content' display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t(
                'pages.fundraising_campaign.components.fundraising_campaign_filters_box.fundraising_campaign_filters_box_status_label',
              )}
            </Text>
            <SelectField {...register('status')}>
              <option value=''>
                {t(
                  'pages.fundraising_campaign.components.fundraising_campaign_filters_box.fundraising_campaign_filters_box_status_all_option',
                )}
              </option>
              <option value={FundraisingCampaignStatus.PUBLISHED}>
                {t(
                  'pages.fundraising_campaign.components.fundraising_campaign_filters_box.fundraising_campaign_filters_box_status_published_option',
                )}
              </option>
              <option value={FundraisingCampaignStatus.NOT_PUBLISHED}>
                {t(
                  'pages.fundraising_campaign.components.fundraising_campaign_filters_box.fundraising_campaign_filters_box_status_not_published_option',
                )}
              </option>
              <option value={FundraisingCampaignStatus.SCHEDULED}>
                {t(
                  'pages.fundraising_campaign.components.fundraising_campaign_filters_box.fundraising_campaign_filters_box_status_scheduled_option',
                )}
              </option>
              <option value={FundraisingCampaignStatus.ENDED}>
                {t(
                  'pages.fundraising_campaign.components.fundraising_campaign_filters_box.fundraising_campaign_filters_box_status_ended_option',
                )}
              </option>
            </SelectField>
          </InputGroup>

          <DateRangeFilterFields
            onRangeChange={handleRangeChange}
            startDate={startDateControl.field.value}
            endDate={endDateControl.field.value}
            dateValue={dateValueControl.field.value}
            onDateValueChange={dateValueControl.field.onChange}
            inputName='partner-actions-date'
            startDateRootProps={{ w: 'fit-content' }}
            endDateRootProps={{ w: 'fit-content' }}
          />
        </Flex>

        <Button alignSelf={{ lg: 'end' }} colorScheme='text' variant='outline' mt='10px' onClick={() => reset()}>
          {t(
            'pages.fundraising_campaign.components.fundraising_campaign_filters_box.fundraising_campaign_filters_box_clear_filters_button',
          )}
        </Button>
      </Flex>
    </Box>
  );
};
